import {
  BiCreateFromTemplateRequest,
  BiTemplateSummary,
} from '@euler/model/bi/template';
import { useServiceFactory } from '@euler/services/factory';
import { useCallback, useEffect, useState } from 'react';
import { biErrorMessage } from '../../../functions/biErrors';

export type BiTemplatesState =
  | { kind: 'loading' }
  | { kind: 'ready'; templates: BiTemplateSummary[] }
  | { kind: 'failed' };

/**
 * 从模板新建: the templates, read while the sheet is open, and creating a
 * dashboard from one. `create` answers the new dashboard's id, or null.
 */
export function useBiTemplateCreate(visible: boolean) {
  const { biService } = useServiceFactory();
  const [state, setState] = useState<BiTemplatesState>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!visible) return;
    let active = true;
    setState({ kind: 'loading' });
    setError(null);
    biService
      .templates()
      .then(templates => {
        if (active)
          setState({
            kind: 'ready',
            templates: templates.filter(template => template.available),
          });
      })
      .catch(e => {
        console.error('BI templates failed', e);
        if (active) setState({ kind: 'failed' });
      });
    return () => {
      active = false;
    };
  }, [biService, visible, reload]);
  const retry = useCallback(() => setReload(value => value + 1), []);
  const create = useCallback(
    async (request: BiCreateFromTemplateRequest): Promise<string | null> => {
      setBusy(true);
      setError(null);
      try {
        return (await biService.createDashboardFromTemplate(request)).id;
      } catch (e) {
        console.error('BI template create failed', e);
        setError(biErrorMessage(e, '没有创建成功，请稍后重试。'));
        return null;
      } finally {
        setBusy(false);
      }
    },
    [biService],
  );
  return { state, retry, create, busy, error };
}
